import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Device } from "@twilio/voice-sdk";
import { Phone, PhoneOff, Mic, MicOff, Loader2, X, PhoneForwarded, Monitor, AlertTriangle } from "lucide-react";
import { apiClient, formatApiError } from "../../lib/auth";

const toE164 = (s) => {
  const d = (s || "").replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  if (d.length === 10) return `+1${d}`;
  if ((s || "").trim().startsWith("+")) return `+${d}`;
  return "";
};

const fmtDur = (sec) => `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;

export default function CallModal({ open, convo, personalPhoneDisplay, onClose }) {
  // mode: confirm | browser | cell
  const [mode, setMode] = useState("confirm");
  const [status, setStatus] = useState("idle"); // idle|connecting|in-call|ended|error
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState("");
  const [duration, setDuration] = useState(0);
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const timerRef = useRef(null);

  const leadPhone = convo?.phone || "";
  const e164 = toE164(leadPhone);

  const cleanup = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { callRef.current?.disconnect(); } catch (_e) { /* */ }
    try { deviceRef.current?.destroy(); } catch (_e) { /* */ }
    callRef.current = null; deviceRef.current = null;
  };

  useEffect(() => {
    if (open) { setMode("confirm"); setStatus("idle"); setMuted(false); setError(""); setDuration(0); }
    return () => cleanup();
  }, [open]); // eslint-disable-line

  const startTimer = () => {
    setDuration(0);
    timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
  };

  const startBrowserCall = async () => {
    setMode("browser"); setError(""); setStatus("connecting");
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (_e) {
      setStatus("error"); setError("Microphone access is blocked. Allow mic access in your browser to call from here."); return;
    }
    try {
      const { data } = await apiClient.get("/voice/token");
      const device = new Device(data.token, { codecPreferences: ["opus", "pcmu"] });
      deviceRef.current = device;
      device.on("error", (e) => { setStatus("error"); setError(e?.message || "Call error"); });
      const call = await device.connect({ params: { To: e164 } });
      callRef.current = call;
      call.on("accept", () => { setStatus("in-call"); startTimer(); });
      call.on("disconnect", () => { setStatus("ended"); if (timerRef.current) clearInterval(timerRef.current); });
      call.on("cancel", () => { setStatus("ended"); });
      call.on("error", (e) => { setStatus("error"); setError(e?.message || "Call error"); });
    } catch (e) {
      setStatus("error"); setError(formatApiError(e, "Couldn't place the call. Your Twilio account may be on trial (verified numbers only)."));
    }
  };

  const startCellCall = async () => {
    setMode("cell"); setError(""); setStatus("connecting");
    try {
      const { data } = await apiClient.post("/voice/call-phone", { conversation_id: convo.id });
      setStatus("ringing-cell");
      window.__ringInfo = data;
    } catch (e) {
      setStatus("error"); setError(formatApiError(e, "Couldn't start the call. Your Twilio account may be on trial (verified numbers only)."));
    }
  };

  const toggleMute = () => {
    if (!callRef.current) return;
    const next = !muted; callRef.current.mute(next); setMuted(next);
  };
  const hangup = () => { cleanup(); setStatus("ended"); };
  const close = () => { cleanup(); onClose?.(); };

  if (!open || !convo) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="call-modal">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-md" onClick={mode === "confirm" ? close : undefined} />
      <div className="relative bg-white border border-slate-200 rounded-3xl w-full max-w-sm shadow-[0_30px_80px_-20px_rgba(15,23,42,0.35)] overflow-hidden">
        <button data-testid="call-modal-close" onClick={close} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 z-10"><X className="w-5 h-5" /></button>

        <div className="px-6 pt-8 pb-6 text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4 shadow-[0_12px_30px_-8px_rgba(16,185,129,0.6)]">
            {(convo.name || "?").charAt(0)}
          </div>
          <div className="font-display text-xl font-bold text-slate-900" data-testid="call-lead-name">{convo.name}</div>
          <div className="text-sm text-slate-500 mt-0.5" data-testid="call-lead-number">{leadPhone}</div>

          {mode === "confirm" && (
            <>
              <div className="mt-4 flex items-start gap-2 text-left bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-3 py-2.5 text-xs" data-testid="call-confirm-warning">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Double-check this is the right lead. You're about to call <b>{leadPhone}</b>.</span>
              </div>
              <div className="mt-5 space-y-2.5">
                <button data-testid="call-browser-btn" onClick={startBrowserCall}
                  className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl py-3 text-sm transition-all shadow-sm">
                  <Monitor className="w-4 h-4" /> Call through browser
                </button>
                <button data-testid="call-cell-btn" onClick={startCellCall}
                  className="w-full inline-flex items-center justify-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold rounded-xl py-3 text-sm transition-all">
                  <PhoneForwarded className="w-4 h-4" /> Ring my cell{personalPhoneDisplay ? ` (${personalPhoneDisplay})` : ""} first
                </button>
              </div>
              <p className="text-[11px] text-slate-400 mt-3">Browser calling needs mic access. "Ring my cell" calls your phone, then connects the lead.</p>
            </>
          )}

          {mode === "browser" && (
            <div className="mt-5" data-testid="call-browser-state">
              {status === "connecting" && <div className="inline-flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Connecting…</div>}
              {status === "in-call" && (
                <>
                  <div className="text-emerald-600 font-semibold text-sm">Connected · <span className="tabular-nums" data-testid="call-timer">{fmtDur(duration)}</span></div>
                  <div className="mt-5 flex items-center justify-center gap-4">
                    <button data-testid="call-mute-btn" onClick={toggleMute} className={`w-12 h-12 rounded-full flex items-center justify-center border transition-all ${muted ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                      {muted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                    </button>
                    <button data-testid="call-hangup-btn" onClick={hangup} className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg transition-all">
                      <PhoneOff className="w-6 h-6" />
                    </button>
                  </div>
                </>
              )}
              {status === "ended" && <div className="text-sm text-slate-500">Call ended{duration ? ` · ${fmtDur(duration)}` : ""}.</div>}
              {status === "error" && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-left" data-testid="call-error">{error}</div>}
            </div>
          )}

          {mode === "cell" && (
            <div className="mt-5" data-testid="call-cell-state">
              {status === "connecting" && <div className="inline-flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Starting call…</div>}
              {status === "ringing-cell" && (
                <div className="text-sm text-slate-600">
                  <PhoneForwarded className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                  Your phone{personalPhoneDisplay ? ` (${personalPhoneDisplay})` : ""} is ringing. <b>Pick up</b> and we'll connect you to {convo.name}.
                </div>
              )}
              {status === "error" && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-left" data-testid="call-error">{error}</div>}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
